<script setup lang="ts">
import { router } from '@inertiajs/vue3';
import { computed, ref } from 'vue';
import { defineAsyncComponent, type Component } from 'vue';
import { useI18n } from 'vue-i18n';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar/index';
import { Button } from '@/components/ui/button/index';
import { Separator } from '@/components/ui/separator/index';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet/index';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs/index';
import { ImageLightbox } from '@ja/core-ui/composites/image-lightbox/index';
import { useApiQuery } from '@ja/portal-query';

import { isViewerPathEmpty } from '../composables/useFieldVisibility';
import { useTranslatable } from '../composables/useTranslatable';
import { useViewerSheet } from '../composables/useViewerSheet';
import { useViewerTabs } from '../composables/useViewerTabs';
import { getBlockComponent, hasBlockComponent } from '../services/componentRegistry';
import { autoFormat, formatDate, looksLikeDate } from '../services/formatters';
import type { BlockConfig, FieldConfig, TabConfig, ViewerConfig } from '../types';
import { edit, viewerDetail } from '../wayfinder/routes';

import ViewerFieldDisplay from './ViewerFieldDisplay.vue';

const asyncBlockCache = new Map<string, Component>();
function getViewerBlockComponent(componentName: string): Component | null {
    if (!componentName) return null;
    if (asyncBlockCache.has(componentName)) return asyncBlockCache.get(componentName)!;
    const entry = getBlockComponent(componentName);
    if (!entry) return null;
    if (typeof entry === 'function') {
        const asyncComp = defineAsyncComponent(entry as () => Promise<{ default: Component }>);
        asyncBlockCache.set(componentName, asyncComp);
        return asyncComp;
    }
    const resolved = (entry as { default: Component }).default ?? null;
    if (resolved) asyncBlockCache.set(componentName, resolved);
    return resolved;
}
function hasCustomBlock(block: BlockConfig): boolean {
    return !!block.component && hasBlockComponent(block.component);
}

// Tab rendering helpers shared with CrudViewer (component lookup, icon
// resolution, synthetic-block wrapping for tab-level fields).
const { getViewerTabComponent, isCustomTab, getIcon, blocksForTab } = useViewerTabs();

interface ViewerDetailResponse {
    item: Record<string, unknown>;
    model: string;
    id: string;
    viewer: ViewerConfig;
    translationPrefix: string | null;
    titles: { singular: string; plural: string };
}

const sheet = useViewerSheet();
const { t, locale } = useI18n();

// Fetch data via TanStack Query with caching
const detailQueryKey = computed(() => ['viewer-detail', sheet.currentModel, String(sheet.currentId)]);
const detailUrl = computed(() => {
    if (!sheet.currentModel || !sheet.currentId) return '';
    return viewerDetail.url({ model: sheet.currentModel, id: String(sheet.currentId) });
});

const { data: detail, isLoading, isPlaceholderData } = useApiQuery<ViewerDetailResponse>(
    detailQueryKey,
    detailUrl,
    {},
    {
        enabled: computed(() => sheet.isOpen && !!sheet.currentModel && !!sheet.currentId),
        staleTime: 30_000,
        placeholderData: (previousData: any) => previousData,
    },
);

// Config for translations
const configForTranslation = computed(() => {
    if (!detail.value) return undefined;
    return {
        translationPrefix: detail.value.translationPrefix,
        titles: detail.value.titles,
    } as any;
});

const { trField, trBlock, trTab, trBadge, trValue } = useTranslatable(configForTranslation);

// Viewer config from response
const viewerConfig = computed(() => detail.value?.viewer);
const displayItem = computed(() => detail.value?.item ?? {});

// Resolve a dot-notated path (e.g. 'client.name') from a nested object
function resolveDotPath(obj: Record<string, unknown> | undefined, path: string): unknown {
    if (!obj) return undefined;
    let value: unknown = obj;
    for (const key of path.split('.')) {
        if (value === null || value === undefined || typeof value !== 'object') return undefined;
        value = (value as Record<string, unknown>)[key];
    }
    return value;
}

// Title and subtitle (auto-format dates, support dot notation)
const title = computed(() => {
    const field = viewerConfig.value?.titleField ?? 'id';
    const raw = resolveDotPath(displayItem.value, field) ?? sheet.currentId ?? '';
    return looksLikeDate(raw) ? formatDate(raw) : raw;
});

const subtitle = computed(() => {
    const field = viewerConfig.value?.subtitleField;
    if (!field) return null;
    const raw = resolveDotPath(displayItem.value, field) ?? null;
    if (raw === null || raw === undefined || raw === '') return null;
    return looksLikeDate(raw) ? formatDate(raw) : trValue(field, raw as string | number | boolean, String(raw));
});

// Header component (custom widget rendered in avatar/header position)
const headerComponentName = computed(() => viewerConfig.value?.headerComponent ?? null);
const headerComponentResolved = computed(() => {
    const name = headerComponentName.value;
    if (!name) return null;
    return getViewerBlockComponent(name);
});
const headerComponentProps = computed(() => viewerConfig.value?.headerComponentProps ?? null);

// Avatar from media collection
const avatarCollection = computed(() => viewerConfig.value?.avatarCollection ?? null);
const avatarQueryKey = computed(() => ['media', sheet.currentModel, String(sheet.currentId), avatarCollection.value]);
const avatarApiUrl = computed(
    () => `/crud/${sheet.currentModel}/${sheet.currentId}/media?collection=${encodeURIComponent(avatarCollection.value ?? '')}`,
);

interface MediaFile { thumb_url: string; original_url: string; file_name: string }

const { data: avatarMedia } = useApiQuery<MediaFile[]>(
    avatarQueryKey,
    avatarApiUrl,
    {},
    { enabled: computed(() => !!avatarCollection.value && !!sheet.currentModel && !!sheet.currentId && !isLoading.value) },
);

const avatarUrl = computed(() => avatarMedia.value?.[0]?.thumb_url ?? null);
const avatarOriginalUrl = computed(() => avatarMedia.value?.[0]?.original_url ?? null);
const avatarFileName = computed(() => avatarMedia.value?.[0]?.file_name ?? '');
const lightboxOpen = ref(false);

// Header fields
const headerFields = computed(() => viewerConfig.value?.headerFields ?? []);

// Tabs support — prune tabs whose hideWhenEmpty paths are all empty on the
// current item. Shares isViewerPathEmpty with CrudViewer.vue so the
// quick-view dialog and full viewer page agree on which tabs to show.
const tabs = computed<TabConfig[]>(() => {
    const allTabs = viewerConfig.value?.tabs ?? [];
    const item = displayItem.value as Record<string, unknown>;
    return allTabs.filter((tab) => {
        const paths = (tab as TabConfig & { hideWhenEmpty?: string[] }).hideWhenEmpty ?? [];
        if (!Array.isArray(paths) || paths.length === 0) return true;
        return paths.some((path) => !isViewerPathEmpty(item, path));
    });
});
const hasTabs = computed(() => tabs.value.length > 0);

// Default tab via computed getter/setter (no watcher needed)
const _userTab = ref('');
const currentTab = computed({
    get: () => {
        if (_userTab.value && tabs.value.some(t => t.key === _userTab.value)) return _userTab.value;
        return tabs.value[0]?.key ?? '';
    },
    set: (v: string) => { _userTab.value = v; },
});

// Actions (exclude 'view' — we're already viewing; exclude 'delete' — use Lister/bulk for that)
const actions = computed(() =>
    (viewerConfig.value?.actions ?? []).filter(
        (a) => a.name !== 'view' && a.name !== 'delete',
    ),
);

function navigateToEdit() {
    sheet.closeViewerSheet();
    router.visit(edit({ model: sheet.currentModel, id: String(sheet.currentId) }).url);
}

function handleAction(action: any) {
    if (action.name === 'edit') {
        navigateToEdit();
        return;
    }

    if (action.navigate) {
        sheet.closeViewerSheet();
        const url = action.navigate.replace(':id', String(sheet.currentId));
        router.visit(url);
        return;
    }

    if (action.route) {
        const url = action.route.replace(':id', String(sheet.currentId));
        const method = (action.method?.toLowerCase() ?? 'post') as 'post' | 'put' | 'patch' | 'delete';
        if (method === 'delete' || method === 'post' || method === 'put' || method === 'patch') {
            router[method](url, {}, { preserveScroll: true });
        }
        sheet.closeViewerSheet();
    }
}

// Get field value from display item (supports dot notation keys)
function getFieldValue(field: FieldConfig): unknown {
    const keys = field.key.split('.');
    let value: unknown = displayItem.value;
    for (const key of keys) {
        if (value === null || value === undefined || typeof value !== 'object') return null;
        value = (value as Record<string, unknown>)[key];
    }
    return value ?? field.default ?? null;
}

// Format value for display in header fields
function formatHeaderValue(key: string): string {
    const value = resolveDotPath(displayItem.value, key);
    if (value === null || value === undefined || value === '') {
        return '—';
    }

    if (looksLikeDate(value)) {
        return formatDate(value, locale.value);
    }

    return trValue(key, value as string | number | boolean, autoFormat(value, t('crud.yes'), t('crud.no'), locale.value));
}
</script>

<template>
    <Sheet :open="sheet.isOpen" @update:open="(v) => { if (!v) sheet.closeViewerSheet(); }">
        <SheetContent side="right" class="sm:max-w-2xl w-full overflow-y-auto px-6 data-[state=open]:duration-300">
            <!-- Hidden header for accessibility when showHeader is false -->
            <SheetHeader v-if="viewerConfig?.showHeader === false" class="sr-only">
                <SheetTitle>{{ title }}</SheetTitle>
            </SheetHeader>
            <!-- Visible header -->
            <SheetHeader v-else class="pb-4">
                <div class="flex items-start gap-5">
                    <!-- Custom header component (e.g., ScoreCircle) -->
                    <div v-if="!isLoading && headerComponentResolved" class="shrink-0">
                        <component
                            :is="headerComponentResolved"
                            :form="displayItem"
                            :item="displayItem"
                            :model="sheet.currentModel"
                            :item-id="sheet.currentId"
                            :component-props="headerComponentProps"
                        />
                    </div>
                    <!-- Avatar from media collection -->
                    <Avatar
                        v-else-if="!isLoading && avatarCollection"
                        class="size-40 shrink-0"
                        :class="avatarUrl ? 'cursor-pointer ring-offset-background hover:ring-2 hover:ring-ring hover:ring-offset-2 transition-shadow' : ''"
                        @click="avatarUrl && (lightboxOpen = true)"
                    >
                        <AvatarImage v-if="avatarUrl" :src="avatarUrl" class="object-cover" />
                        <AvatarFallback class="text-3xl">
                            {{ String(title).charAt(0).toUpperCase() }}
                        </AvatarFallback>
                    </Avatar>
                    <div class="min-w-0 flex-1 pt-1">
                        <SheetTitle class="text-xl">
                            <template v-if="isLoading">
                                <div class="bg-muted h-7 w-48 animate-pulse rounded" />
                            </template>
                            <template v-else>
                                {{ title }}
                            </template>
                        </SheetTitle>
                        <SheetDescription v-if="subtitle && !isLoading">
                            {{ subtitle }}
                        </SheetDescription>
                    </div>
                </div>
            </SheetHeader>

            <Transition name="viewer-fade" mode="out-in">
                <!-- Loading state -->
                <div v-if="isLoading" key="skeleton" class="space-y-4 py-2">
                    <div class="bg-muted h-4 w-full animate-pulse rounded" />
                    <div class="bg-muted h-4 w-3/4 animate-pulse rounded" />
                    <div class="bg-muted h-32 w-full animate-pulse rounded" />
                </div>

                <!-- Content -->
                <div v-else-if="viewerConfig" key="content" :class="[{ 'opacity-50 transition-opacity': isPlaceholderData }, viewerConfig.showHeader === false ? 'pt-6' : '']">
                    <!-- Action buttons -->
                    <div v-if="actions.length > 0" class="flex items-center gap-2 pb-4">
                        <Button
                            v-for="action in actions.filter((a) => !a.hidden)"
                            :key="action.name"
                            :variant="(action.color as any) ?? (action.name === 'edit' ? 'default' : 'outline')"
                            size="sm"
                            @click="handleAction(action)"
                        >
                            {{ t(`crud.actions.${action.name}`) }}
                        </Button>
                    </div>

                    <!-- Header fields -->
                    <div v-if="headerFields.length > 0" class="text-muted-foreground flex flex-wrap items-center gap-4 pb-4 text-sm">
                        <div v-for="fieldKey in headerFields" :key="fieldKey" class="flex items-center gap-2">
                            <span class="font-medium">{{ trField(fieldKey) }}:</span>
                            <span>{{ formatHeaderValue(fieldKey) }}</span>
                        </div>
                    </div>

                    <Separator v-if="headerFields.length > 0 || actions.length > 0" class="mb-4" />

                    <!-- Tabbed layout -->
                    <Tabs v-if="hasTabs" v-model="currentTab" class="space-y-4">
                        <TabsList class="w-full max-w-full justify-start gap-1 overflow-x-auto">
                            <TabsTrigger
                                v-for="tab in tabs"
                                :key="tab.key"
                                :value="tab.key"
                                class="shrink-0 gap-2 whitespace-nowrap"
                            >
                                <component v-if="tab.icon && getIcon(tab.icon)" :is="getIcon(tab.icon)" class="size-4" />
                                {{ trTab(tab.key) }}
                            </TabsTrigger>
                        </TabsList>

                        <TabsContent
                            v-for="tab in tabs"
                            :key="tab.key"
                            :value="tab.key"
                            class="space-y-6"
                        >
                            <!-- Custom tab component -->
                            <component
                                v-if="isCustomTab(tab) && getViewerTabComponent(tab.component!)"
                                :is="getViewerTabComponent(tab.component!)!"
                                :tab="tab"
                                :item="displayItem"
                                :model="sheet.currentModel"
                                :item-id="sheet.currentId"
                                :tr-field="trField"
                                :tr-tab="trTab"
                                :tr-block="trBlock"
                                :translation-prefix="configForTranslation?.translationPrefix ?? null"
                                :component-props="tab.componentProps ?? undefined"
                            />
                            <!-- Standard blocks -->
                            <template v-else>
                            <p v-if="blocksForTab(tab).length === 0" class="text-muted-foreground text-sm italic">
                                {{ t('crud.empty_tab') }}
                            </p>
                            <section v-for="block in blocksForTab(tab)" :key="block.key" class="@container/block">
                                <!-- Custom block components render their own title/card -->
                                <template v-if="hasCustomBlock(block as BlockConfig) && getViewerBlockComponent((block as BlockConfig).component!)">
                                    <component
                                        :is="getViewerBlockComponent((block as BlockConfig).component!)!"
                                        :block="block"
                                        :form="displayItem"
                                        :item="displayItem"
                                        :model="sheet.currentModel"
                                        :item-id="sheet.currentId"
                                        :tr-block="trBlock"
                                        :tr-field="trField"
                                        :translation-prefix="configForTranslation?.translationPrefix ?? null"
                                        :component-props="(block as BlockConfig).componentProps ?? undefined"
                                    />
                                </template>
                                <!-- Standard field block -->
                                <template v-else>
                                    <h3 class="text-foreground mb-3 text-sm font-semibold tracking-wide uppercase">{{ trBlock(block.key) }}</h3>
                                    <dl class="@[400px]/block:grid-cols-2 grid grid-cols-1 gap-x-6 gap-y-4">
                                        <ViewerFieldDisplay
                                            v-for="field in (block as BlockConfig).fields"
                                            :key="field.key"
                                            :field="field"
                                            :value="getFieldValue(field)"
                                            :tr-field="trField"
                                            :tr-badge="trBadge"
                                            :item="displayItem"
                                        />
                                    </dl>
                                </template>
                                <Separator class="mt-6 last:hidden" />
                            </section>
                            </template>
                        </TabsContent>
                    </Tabs>

                    <!-- Flat blocks layout -->
                    <div v-else-if="viewerConfig.blocks?.length" class="space-y-6">
                        <section v-for="block in viewerConfig.blocks" :key="block.key" class="@container/block">
                            <!-- Custom block components render their own title/card -->
                            <template v-if="hasCustomBlock(block) && getViewerBlockComponent(block.component!)">
                                <component
                                    :is="getViewerBlockComponent(block.component!)!"
                                    :block="block"
                                    :form="displayItem"
                                    :item="displayItem"
                                    :model="sheet.currentModel"
                                    :item-id="sheet.currentId"
                                    :tr-block="trBlock"
                                    :tr-field="trField"
                                    :translation-prefix="configForTranslation?.translationPrefix ?? null"
                                    :component-props="block.componentProps ?? undefined"
                                />
                            </template>
                            <!-- Standard field block -->
                            <template v-else>
                                <h3 class="text-foreground mb-3 text-sm font-semibold tracking-wide uppercase">{{ trBlock(block.key) }}</h3>
                                <dl class="@[400px]/block:grid-cols-2 grid grid-cols-1 gap-x-6 gap-y-4">
                                    <ViewerFieldDisplay
                                        v-for="field in block.fields"
                                        :key="field.key"
                                        :field="field"
                                        :value="getFieldValue(field)"
                                        :tr-field="trField"
                                        :tr-badge="trBadge"
                                        :item="displayItem"
                                    />
                                </dl>
                            </template>
                            <Separator class="mt-6 last:hidden" />
                        </section>
                    </div>

                    <!-- Metadata footer (created_at / updated_at) -->
                    <div v-if="viewerConfig.showMetadata !== false && (displayItem.created_at || displayItem.updated_at)" class="border-t border-dashed pt-4 mt-6">
                        <dl class="text-muted-foreground flex flex-wrap gap-x-6 gap-y-1 text-xs">
                            <div v-if="displayItem.created_at" class="flex items-center gap-1.5">
                                <dt class="font-medium">{{ t('crud.created_at') }}</dt>
                                <dd>{{ formatDate(displayItem.created_at) }}</dd>
                            </div>
                            <div v-if="displayItem.updated_at" class="flex items-center gap-1.5">
                                <dt class="font-medium">{{ t('crud.updated_at') }}</dt>
                                <dd>{{ formatDate(displayItem.updated_at) }}</dd>
                            </div>
                        </dl>
                    </div>
                </div>
            </Transition>
        </SheetContent>
    </Sheet>

    <ImageLightbox
        v-if="avatarOriginalUrl"
        v-model:open="lightboxOpen"
        :src="avatarOriginalUrl ? `${avatarOriginalUrl}${avatarOriginalUrl.includes('?') ? '&' : '?'}preview=1` : ''"
        :alt="avatarFileName"
        :download-href="avatarOriginalUrl ? `${avatarOriginalUrl}${avatarOriginalUrl.includes('?') ? '&' : '?'}download=1` : undefined"
    />
</template>

<style scoped>
.viewer-fade-enter-active,
.viewer-fade-leave-active {
    transition: opacity 150ms ease;
}
.viewer-fade-enter-from,
.viewer-fade-leave-to {
    opacity: 0;
}
</style>
